<template>
  <div
    class="view relative flex size-full flex-1 flex-col"
    @dragenter="onDragEnter"
    @dragover="onDragOver"
    @keydown.esc.stop="closeIfNoChanges"
    v-if="user"
  >

    <!-- Teleport header toggle button for mobile task view -->
    <teleport to="#header-actions" v-if="readyToTeleport">
      
      <SimpleButton
        v-if="(mobileMode || isTablet) && task?.isParentTask"
        class="rightbar-toggle p-1"
        :variant="mobileSubtasksOpen ? 'primary' : 'ghost'"  
        :title="$t('ui.task_dialog_subtasks')"
        @click="mobileSubtasksOpen = !mobileSubtasksOpen"
      >
        <IconSquares2x216Solid class="text-2xl" />
      </SimpleButton>

      <SimpleButton
        v-if="mobileMode && task?.parentTaskId"
        class="rightbar-toggle p-1"
        :variant="mobileParentOpen ? 'primary' : 'ghost'"
        :title="$t('ui.task_dialog_parent_task')"
        @click="mobileParentOpen = !mobileParentOpen; parentEvents = []; "
      >
        <IconArrowMergeAltLeft class="text-2xl" />

      </SimpleButton>

      <SimpleButton
        v-if="mobileMode && route.name === 'projectTask'"
        class="rightbar-toggle p-1"
        :variant="rightBarOpen ? 'primary' : 'ghost'"
        :title="t('ui.task_dialog_toggle_rightbar')"
        @click="toggleRightBar"
      >
        <IconLayoutSidebarRightFilled class="text-2xl" />
      </SimpleButton>

      
    </teleport>

    <ManualTimeDialog
      v-if="manualTimeDialog"
      :taskId="task.id"
      @close="manualTimeDialog = false"
    />
    <div
      class="dnd absolute z-[calc(var(--z-panel)+2)] hidden size-full items-center justify-center bg-white text-[6.25rem] opacity-80 [&.active]:flex"
      ref="dnd" @dragleave="onDragLeave" @drop="onDrop"
    >{{ $t('ui.task_dialog_drop') }}</div>
    <div v-if="project && project.backgroundUrl"></div>
    <div class="dlg-row flex min-h-px grow max-sm:flex-col">
      <div
        class="dlg-content relative flex min-h-0 min-w-0 flex-1 gap-6 sm:pr-6 max-sm:flex-col
               before:pointer-events-none before:absolute before:inset-0 before:-z-1 before:bg-board-scrim before:opacity-70 before:content-['']"
      >
      <div
        class="parent-log log-wrapper flex min-h-full flex-1 flex-col items-stretch shadow-[inset_0_-2px_5px_var(--shadow-color)] max-sm:m-0 max-sm:min-h-auto"
        :class="mobileMode
          ? `fixed top-(--mobile-header-height) right-0 bottom-(--mobile-nav-height) z-[calc(var(--z-panel)+1)] w-screen max-w-screen
             overflow-y-auto bg-chrome py-5 pr-2.5 pl-5 [backdrop-filter:var(--glass-blur)] [transition:all_0.15s]
             [&_.wrapper-string__empty]:h-7.5`
          : 'relative z-9 max-w-[30%] overflow-y-hidden bg-black/30 pl-4 pb-4'"
        v-if="task?.parentTaskId && (!mobileMode || mobileParentOpen)"
      >
        <div class="log relative flex flex-1 [overflow-y:overlay] [scrollbar-width:thin] max-sm:mx-1.25">
          <Pager
            id="parent-events"
            class="comments-pager no-attach parent-pager flex-1 h-[unset]! [&::-webkit-scrollbar-track]:my-2.5 sm:pr-4"
            :dontLoadAtStart="false"
            :pageLoadRequest="loadParentEvents"
            :count="20"
            :loaderCount="1"
            order="DESC"
            :externalNextKey="parentEvents.length"
            reversed
            category="taskEvent"
            @more="() => { parentMoreEvents = true; }"
            @nomore="() => { parentMoreEvents = false; }"
          >
            <template #default>
              <div class="task-event-wrapper mb-2.5 flex flex-col gap-5 pb-2.5 max-sm:pt-0">
                <div
                  class="task-event flex flex-col items-start first:mt-2.5! max-sm:max-h-none!"
                  :class="{
                    'small max-h-4.5': MANUAL_TIME_ACTIONS.includes(e.action),
                    'same-user -mt-3.75 [&_.comment-wrapper_.not-read]:top-0! [&_.comment-wrapper_.title]:mt-0!': !isParentCommentSameUser(index),
                  }"
                  v-for="(e, index) in parentSortedEvents"
                  v-show="e.taskComment ? hasCommentText(e.taskComment) : parentLogsShown"
                  :key="e.id || `parent_${index}`"
                >
                  <Comment
                    v-if="e.taskComment"
                    class="comment"
                    :comment="e.taskComment"
                    :grouped="!isParentCommentSameUser(index)"
                    :user="user"
                    :project="project"
                    :task="parentTaskObj"
                    :tagList="getPMs"
                    :eventAction="e.action"
                    :pinnedCount="pinnedTaskComments.length"
                    readOnly
                  />
                  <TaskEvent
                    v-else
                    class="ml-10.25 self-stretch"
                    :task="parentTaskObj"
                    :taskEvent="e"
                  />
                </div>
              </div>
            </template>
            <template #loader>
              <div class="event-loader mb-5 flex items-center">
                <SkeletonLoader size="small" type="avatar" />
                <div class="event-loader-text ml-2 flex flex-col">
                  <div class="event-loader-comment-data-text mb-2 flex items-center">
                    <SkeletonLoader style="margin-right: 8px;" type="text" width="45" />
                    <SkeletonLoader type="text" size="small" width="23" />
                  </div>
                  <SkeletonLoader type="text" width="242" />
                </div>
              </div>
            </template>
          </Pager>
        </div>
        <div class="mt-2 flex gap-2">
          <SimpleButton size="sm" class="gap-1 bg-chrome! [backdrop-filter:var(--glass-blur)]" @click="returnToParent">
            <IconArrowLeftSolid class="size-4" />
            {{ $t('ui.task_dialog_return_to_parent') }}
          </SimpleButton>
          <SimpleButton
            size="sm"
            class="w-36 justify-between! bg-chrome! [backdrop-filter:var(--glass-blur)]"
            @click="parentLogsShown = !parentLogsShown"
          >
            {{ parentLogsShown ? $t('ui.task_dialog_logs_shown') : $t('ui.task_dialog_comments_only') }}
            <ToggleSwitch :model-value="parentLogsShown" small class="pointer-events-none flex-none!" />
          </SimpleButton>
        </div>
      </div>
      <div class="log-wrapper z-9 flex min-h-full flex-1 flex-col items-stretch overflow-y-hidden pb-4 max-sm:m-0 max-sm:min-h-auto max-sm:p-0"
        :class="{ 'pl-6': !task?.parentTaskId }"
      >
        <div
          v-if="pinnedTaskComments && pinnedTaskComments.length"
          class="pinned-comments mt-2 mr-2 mb-2 rounded-card border border-line p-2.5 hover:bg-hover max-sm:mx-1.25 max-sm:mt-0"
        >
          <div class="flex h-13 items-stretch gap-2" @click.capture="onPinnedSwiperAreaClick">
            <div
              v-if="pinnedTaskComments.length === 1"
              class="pinned-bullet pinned-bullet-single w-1 cursor-pointer rounded-xs bg-accent [transform:scale(1.15)]
                     [transition:opacity_0.15s_ease,transform_0.15s_ease,background-color_0.15s_ease]"
            ></div>
            <div
              v-else
              class="pinned-pagination flex w-1 flex-col justify-center gap-1 static! transform-none!
                     [--swiper-pagination-bullet-width:0.25rem] [--swiper-pagination-bullet-border-radius:0.125rem]
                     [--swiper-pagination-bullet-height:calc((3rem_-_(var(--pin-bullets-count)_-_1)_*_0.25rem)_/_var(--pin-bullets-count))]
                     [--swiper-pagination-bullet-inactive-opacity:0.45] [--swiper-pagination-bullet-vertical-gap:0]
                     [--swiper-pagination-color:var(--accent)] [--swiper-pagination-bullet-inactive-color:var(--text-muted)]"
              :style="{ '--pin-bullets-count': pinnedTaskComments.length }"
            ></div>
            
            <div class="flex flex-col gap-1 flex-1">
              <div class="text-text-muted text-label">
                {{ $t('comments.pinned_comments') }}
                <span v-if="pinnedTaskComments.length > 1" class="text-accent">#{{ pinnedTaskComments.length - pinnedActiveIndex }}</span>
              </div>
              <swiper
                direction="vertical"
                :slidesPerView="1"
                :loop="pinnedTaskComments.length > 1"
                :allowTouchMove="false"
                :simulateTouch="false"
                effect="fade"
                :fadeEffect="{ crossFade: true }"
                :watchSlidesProgress="true"
                :modules="modules"
                :pagination="{
                  el: '.pinned-pagination',
                  clickable: true,
                  renderBullet: (index, className) => `<span class='${className} pinned-bullet bg-muted [transition:opacity_0.15s_ease,transform_0.15s_ease,background-color_0.15s_ease] [&.swiper-pagination-bullet-active]:[transform:scale(1.15)]'></span>`,
                }"
                class="slider-container w-full flex-1 overflow-hidden"
                @swiper="onPinnedSwiperInit"
                @slideChange="pinnedActiveIndex = $event.realIndex"
              >
                <swiper-slide
                  v-for="p in pinnedTaskComments"
                  :key="p.id"
                  class="pinned-slide cursor-pointer"
                >
                  <PinnedComment
                    v-if="p?.taskComment"
                    class="comment"
                    :comment="p.taskComment"
                    :user="user"
                    :task="task"
                    :tagList="getPMs"
                    @open="scrollToPinnedComment"
                  />
                </swiper-slide>
              </swiper>
            </div>
          </div>
        </div>
        <div class="log relative flex flex-1 [overflow-y:overlay] [scrollbar-width:thin] max-sm:mx-1.25" :class="{ 'screenshots-is-open flex-row justify-center pt-6.25': screeenshotsIsOpen, 'has-pins': pinnedTaskComments && pinnedTaskComments.length }">
          <div v-if="screeenshotsIsOpen">
            <h2 class="px-2 text-text">{{ $t("task.screenshots_from_tracked_time") }}</h2>
            <RecycleScroller
              class="screenshots-scroller"
              :items="sortedScreenshots"
              :gridItems="mobileMode ? 2 : 4"
              :class="{ 'mobile-mode': mobileMode }"
              :itemSize="130 + 20"
              :itemSecondarySize="160 + 20"
              key-field="id"
              @scroll-end="loadMoreScreenshots"
            >
              <template #default="{ item }">
                <Screenshot
                  :screenshot="item.value"
                  :task="task"
                  :user="user"
                  class="screenshot"
                  @delete="actionDeleteScreenshot"
                />
              </template>

              <template #after>
                <div class="wrapper-scroller">
                  <div v-if="loadingScreenshots" class="skeleton-wrapper">
                    <div
                      v-for="sc in mobileMode ? 2 : 4"
                      :key="`item_sc_${sc}`"
                      class="screenshot-loader"
                    >
                      <div class="screenshot__item screenshot-loader__image">
                        <SkeletonLoader type="image" width="160" height="95" />
                        <div class="screenshot-loader__info">
                          <SkeletonLoader type="text" width="15" height="15" />
                          <SkeletonLoader type="text" width="26" height="15" />
                          <SkeletonLoader type="text" width="85" height="15" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  class="absolute top-1/2 left-1/2 -translate-1/2 text-center text-lg text-text-muted"
                  v-if="!loadingScreenshots && !sortedScreenshots.length"
                >
                  <div
                    v-if="
                      !project.enableScreenshots &&
                      (userIsProjectAdmin || isWsAdmin)
                    "
                  >
                    {{ $t("task.No_screenshots_here_disable_admin_part1") }}
                    <span @click="goToProjectSettings()" class="cursor-pointer text-text">{{
                      $t("task.No_screenshots_here_disable_admin_part2")
                    }}</span>
                    {{ $t("task.No_screenshots_here_disable_admin_part3") }}
                  </div>
                  <div
                    v-else-if="
                      !project.enableScreenshots &&
                      !(userIsProjectAdmin || isWsAdmin)
                    "
                  >
                    {{ $t("task.No_screenshots_here_disable") }}
                  </div>
                  <div v-else>{{ $t("task.No_screenshots_here_enable") }}</div>
                </div>
              </template>
            </RecycleScroller>
          </div>
          <Pager
            v-else-if="task && task.taskEvents"
            id="events"
            ref="events"
            class="comments-pager no-attach flex-1 h-[unset]! [&::-webkit-scrollbar-track]:my-2.5"
            :dontLoadAtStart="true"
            :pageLoadRequest="loadEvents"
            :count="20"
            :loaderCount="1"
            :reversed="eventsAreReversed"
            :order="eventsAreReversed ? 'DESC' : 'ASC'"
            :externalNextKey="task.taskEvents.length"
            
            category="taskEvent"
            @more="() => { moreEvents = true; }"
            @nomore="() => { moreEvents = false; }"
          >
            <template #default>
              <div class="task-event-wrapper mb-2.5 flex flex-col gap-5 pb-2.5 max-sm:pt-0">
                  <!-- <pre> {{  JSON.stringify(task, null, 2) }}</pre> -->
                <div
                  class="task-event flex flex-col items-start first:mt-2.5! max-sm:max-h-none!"
                  :class="{
                    'small max-h-4.5': MANUAL_TIME_ACTIONS.includes(e.action),
                    'same-user -mt-3.75 [&_.comment-wrapper_.not-read]:top-0! [&_.comment-wrapper_.title]:mt-0!': !isCommentSameUser(index),
                    'scroll-target-highlight': highlightedElementId === eventElementId(e),
                    '[&>.comment-wrapper]:bg-row-alt': highlightedElementId === eventElementId(e) && !MANUAL_TIME_ACTIONS.includes(e.action),
                  }"
                  :id="eventElementId(e)"
                  v-show="!hiddenCommentIds.has(e.taskComment?.id) && (logsShown || e.taskComment)"
                  v-for="(e, index) in sortedEvents"
                  :key="e.id"
                >
                  <Comment
                    v-if="e.taskComment"
                    class="comment"
                    :comment="e.taskComment"
                    :grouped="!isCommentSameUser(index)"
                    :user="user"
                    :project="project"
                    :task='task'
                    :tagList="getPMs"
                    :ref='`comment-${index}`'
                    :notRead="isread(e.taskComment)"
                    :eventAction="e.action"
                    :pinned="isCommentPinned(e.taskComment.id)"
                    :pinnedCount="pinnedTaskComments.length"
                    @pinChanged="onPinChanged"
                    @update="actionUpdateComment"
                    @delete="deleteComment"
                    @setpreview="actionSetTaskPreview"
                    @markAsRead="markAsReadSingle(e.taskComment)"
                    @openAppRunner="openAppRunner"
                    @editStateChanged="onCommentEditStateChanged"
                    @hiddenChanged="onCommentHiddenChanged"
                    @commentAttachImage="attachFilesToComment(e.taskComment.id, $event)"
                  >
                    <template v-if="e.action === 'task_create_comment'">
                      <SimpleButton
                        v-if='!task.checklist || task.checklist === "[]"'
                        class="mt-4"
                        :text="$t('task.ADD_A_CHECKLIST')"
                        :pressed="checklistActive"
                        @click='() => checklistActive = !checklistActive'/>
                      <Checklist
                        v-if="showChecklist"
                        :checklist='task.checklist'
                        @update='updateChecklist'
                        @cancel="checklistActive = false"
                        :autoFocus='!task.checklist || task.checklist === "[]"'
                      />
                    </template>
                  </Comment>
                  <TaskEvent
                    v-else
                    class="ml-10.25 self-stretch"
                    :task="task"
                    :taskEvent="e"
                    :highlighted="highlightedElementId === eventElementId(e)"
                  />
                </div>
              </div>
            </template>
            <template #loader >
              <div class="event-loader mb-5 flex items-center">
                <SkeletonLoader
                  size="small"
                  type="avatar"
                />
                <div class="event-loader-text ml-2 flex flex-col">
                  <div class="event-loader-comment-data-text mb-2 flex items-center">
                    <SkeletonLoader
                      style="margin-right: 8px;"
                      type="text"
                      width="45"
                    />
                    <SkeletonLoader
                      type="text"
                      size="small"
                      width="23"
                    />
                  </div>
                  <SkeletonLoader
                    type="text"
                    width="242"
                  />
                </div>
              </div>
            </template>
          </Pager>
          <div v-else class="pager mt-auto mb-2.5 flex-1">
            <div class="event-loader mb-5 flex items-center">
              <SkeletonLoader
                size="small"
                type="avatar"
              />
              <div class="event-loader-text ml-2 flex flex-col">
                <div class="event-loader-comment-data-text mb-2 flex items-center">
                  <SkeletonLoader
                    style="margin-right: 8px;"
                    type="text"
                    width="45"
                  />
                  <SkeletonLoader
                    type="text"
                    size="small"
                    width="23"
                  />
                </div>
                <SkeletonLoader
                  type="text"
                  width="242"
                />
              </div>
            </div>
        </div>

          
        </div>
        <div class="mb-2 flex items-center justify-between max-sm:px-4">
          <SimpleButton
            size="sm"
            class="w-36 justify-between! bg-chrome! [backdrop-filter:var(--glass-blur)]"
            @click="logsShown = !logsShown"
          >
            {{ logsShown ? $t('ui.task_dialog_logs_shown') : $t('ui.task_dialog_comments_only') }}
            <ToggleSwitch :model-value="logsShown" small class="pointer-events-none flex-none!" />
          </SimpleButton>
          <SimpleButton
            v-if="showScrollToTopButton"
            size="sm"
            class="bg-chrome! [backdrop-filter:var(--glass-blur)]"
            @click="scrollEventsToTop"
          >
            <IconArrowUp16Solid class="size-3" />
            {{ $t('ui.task_dialog_scroll_to_top') }}
          </SimpleButton>
        </div>
        <EditorInput
          v-if="task && !screeenshotsIsOpen"
          class="comments-input max-sm:mx-4 max-sm:mb-4"
          :title="$t('task.Type_comment')"
          ref='cInput'
          :notFocusOnInit="mobileMode"
          :dontSubmitOnEnter="mobileMode"
          :reversedTag="true"
          :alwaysOpen="true"
          :previewImage="commentFiles"
          @submit="addComment"
          @input="markAsRead()"
          @clicked="markAsRead()"
          @submitWithAttach="submitWithFiles"
          :tagList="getPMs"
          :allowSubmit='task.attachment ? !task.attachments.find(
          a => a.commentId === newCommentId && !a.uploaded) && !painterroOpened : true'
        />

      </div>
      <Transition enter-from-class="opacity-0" leave-to-class="opacity-0" enter-active-class="transition-opacity duration-150" leave-active-class="transition-opacity duration-150">
        <div v-if="(mobileMode || isTablet) && mobileSubtasksOpen" class="fixed inset-0 z-(--z-panel) bg-backdrop" @click="mobileSubtasksOpen = false"></div>
      </Transition>
      <div
        class="subtasks box-border flex h-full flex-col"
        :class="mobileMode || isTablet
          ? [
            `fixed z-[calc(var(--z-panel)+1)] w-72.5 overflow-y-auto border-l border-line p-5 [transition:all_0.15s]
             [&_.task]:w-full! [&_.task]:max-w-full! [&_.task-wrapper]:w-full`,
            mobileMode
              ? 'top-(--mobile-header-height) bottom-(--mobile-nav-height) bg-chrome [backdrop-filter:var(--glass-blur)]'
              : 'top-12.5 bottom-0 bg-popover-solid shadow-(--shadow-popover)',
            mobileSubtasksOpen ? 'right-0' : '-right-70',
          ]
          : 'w-75 pt-8 pb-4'"
          v-if="task?.isParentTask">
        <div class="mb-3 text-accent">{{ $t('ui.task_dialog_subtasks_caption') }}</div>

        <!-- Loading skeleton -->
        <div v-if="subtasks === null" class="subtasks-skeleton">
          <Task :simplified="true" :skeleton="{ rows: ['fullwidth','halfwidth'], tags: true }" class="nodrag" />
          <Task :simplified="true" :skeleton="{ rows: ['threequarters'], projectStage: true }" class="nodrag" />
          <Task :simplified="true" :skeleton="{ rows: ['fullwidth'], tags: true }" class="nodrag" />
        </div>

        <!-- Loaded subtasks list -->
        <div v-else class="v-tracklify-scroll flex min-h-0 flex-auto flex-col">
            <div class="gap-2 flex flex-col subtasks-list overflow-y-auto px-2 py-1 max-sm:flex-none max-sm:overflow-y-visible" ref="subtasksListRef">
              <template v-for="st in sortedSubtasks" :key="st.id">
                <Task class="m-0!" 
                  :task="st" :simplified="false" 
                  :showStageAndProject="true" 
                  @click="openSubtask(st)" 
                  :assigneeSelectList="assigneeSelectList"
                  :assigneeList="project.projectMembers"
                  @update="actionUpdateTask"
                >
                  <template v-slot:label>
                    <TaskLabelSelect 
                      stickLeft
                      :task="st"
                      :project="project" 
                      @set="setLabelForTask" 
                      @click.stop
                    >
                      <template #activator>
                        <div
                          class="flex size-[20px] cursor-pointer items-center justify-center rounded-[7px]
                                 border border-line bg-input text-text-muted transition-colors
                                 hover:border-accent hover:bg-accent-soft hover:text-accent
                                 [.open_&]:border-accent [.open_&]:bg-accent-soft [.open_&]:text-accent"
                          :title="$t('task.Tags')"
                        >
                          <IconTag16Solid class="text-label" />
                        </div>
                      </template>
                    </TaskLabelSelect>
                  </template>
                </Task>
              </template>
            </div>
        </div>
      </div>
      </div>
      <TaskRightBar
        :class="{ rightBarOpen }"
        @closeMobile='closeMobileRightBar'
        :screenshotsPage='screeenshotsIsOpen'
        :transferUser=transferUser
        @scrollToManualTime="scrollToManualTime"
        @leaveScreenshotsPage='screeenshotsIsOpen = false'
        @closeDialog="() => $emit('closeMe')"
        @deleteTaskDialog="onDeleteTask();"
        @screenshotsPage="() => { screeenshotsIsOpen=true; loadMoreScreenshots() }"
        @manualTimeDialog="() => { manualTimeDialog=true }"

        
      ></TaskRightBar>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick, provide } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import dayjs from 'dayjs';
import EditorInput from '@/components/elements/EditorInput';
import SimpleButton from '@/components/elements/SimpleButton';
import SkeletonLoader from '@/components/elements/SkeletonLoader';
import Screenshot from '@/components/elements/Screenshot';
import ManualTimeDialog from '@/components/widgets/ManualTimeDialog';
import ToggleSwitch from '@/components/elements/ToggleSwitch';
import Pager from '@/components/elements/Pager';
import Scrollbar from '@/components/elements/Scrollbar';
import Comment from '@/components/elements/Comment';
import PinnedComment from '@/components/elements/PinnedComment';
import TaskEvent from '@/components/elements/TaskEvent';
import Checklist from '@/components/elements/Checklist';
import eventBus from '@/eventBus';
import TaskRightBar from '@/components/widgets/TaskRightBar';
import Task from '@/components/widgets/Task';
import Sortable from 'sortablejs';
import { editImageWithPainterro, readFileAsDataURL } from '@/store/utils';
import { RecycleScroller } from 'vue-virtual-scroller';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';
import { IconArrowLeft, IconArrowLeft16Solid, IconArrowLeftSolid, IconArrowUp16Solid, IconTag16Solid, IconSquares2X216Solid, IconSquares2x216Solid, IconXMark } from '@iconify-prerendered/vue-heroicons';
import { callAPI } from '@/store/socket';
import { IconLayoutSidebarRightFilled, IconArrowMergeAltLeft } from '@iconify-prerendered/vue-tabler';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { EffectFade, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/pagination';

const modules = [EffectFade, Pagination];

const pinnedSwiper = ref(null);
const pinnedActiveIndex = ref(0);

function onPinnedSwiperInit(swiper) {
  pinnedSwiper.value = swiper || null;
  pinnedActiveIndex.value = swiper?.realIndex || 0;
}

function resetPinnedToLatest() {
  if (!pinnedSwiper.value || pinnedActiveIndex.value === 0) return;
  pinnedSwiper.value.slideToLoop(0);
}

function onPinnedSwiperAreaClick(ev) {
  const swiper = pinnedSwiper.value;
  if (!swiper) return;

  const list = Array.isArray(pinnedTaskComments.value) ? pinnedTaskComments.value : [];
  const idx = typeof swiper?.realIndex === 'number' ? swiper.realIndex : swiper?.activeIndex;
  const row = (typeof idx === 'number' && idx >= 0) ? list[idx] : null;
  const currentCommentId = row?.taskComment?.id;

  if (currentCommentId) {
    scrollToPinnedComment(currentCommentId);
  }

  const el = ev.currentTarget;
  if (!el?.getBoundingClientRect) return;

  const rect = el.getBoundingClientRect();
  const y = ev.clientY - rect.top;
  const isPrev = y < rect.height / 2;

  ev.preventDefault();
  ev.stopPropagation();

  if (isPrev) {
    swiper.slidePrev();
  } else {
    swiper.slideNext();
  }
}

function toggleRightBar() {
  rightBarOpen.value = !rightBarOpen.value;
}

const assigneeSelectList = computed(() => store.getters['task/assigneeSelectList']);


function setLabelForTask(obj) {
  store.dispatch('task/setLabelForTask', obj);
}



// Define emits
const emit = defineEmits(['closeMe', 'update:painterroOpened']);

// Define props (empty in this case)
const props = defineProps({});

// Setup Vue Router, Vuex, and i18n
const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

// Template refs
const dnd = ref(null);
const events = ref(null);
const fullTaskEditor = ref(null);
const cInput = ref(null);

// Reactive data
const rightBarOpen = ref(false);
const mobileSubtasksOpen = ref(false);
const mobileParentOpen = ref(false);
const taskLoaded = ref(false);
const editingCommentIds = ref(new Set());
const hiddenCommentIds = ref(new Set());
const isEditComment = computed(() => editingCommentIds.value.size > 0);
const lastCommentEditClosedAt = ref(0);
provide('isEditComment', isEditComment);
provide('lastCommentEditClosedAt', lastCommentEditClosedAt);
const painterroOpened = ref(false);
const loadingScreenshots = ref(false);
const screeenshotsIsOpen = ref(false);
const moreEvents = ref(false);
const newCommentId = ref(null);
const checklistActive = ref(false);
const commentFiles = ref(null);



const loadedTabNum = ref(0);
const manualTimeDialog = ref(false);
const showScrollToTopButton = ref(false);
const logsShown = ref(true);
const parentLogsShown = ref(false);
const hasCommentText = (comment) => (comment.textMD
  || JSON.parse(comment.textDelta).ops.map(op => (typeof op.insert === 'string' ? op.insert : '')).join('')).trim();
const eventsAreReversed = ref(true);
const transferUser = ref(null);
const elementsNextKey = ref(0);
const loading = ref(false);
// Parent task side log state
const parentEvents = ref([]);
const parentMoreEvents = ref(false);
const parentAttachments = ref([]);

const readyToTeleport = ref(false);

// Scroll-to-comment highlight
const highlightedElementId = ref(null);
const highlightTimeoutId = ref(null);
let lastManualTimeEventId = null;

const MANUAL_TIME_ACTIONS = ['add_manual_time', 'request_manual_time'];

const eventElementId = e => (e.taskComment ? `task-comment-${e.taskComment.id}` : `task-event-${e.id}`);

const parentTaskId = computed(() => task.value?.parentTaskId || null);
const parentTaskObj = computed(() => {
  if (!parentTaskId.value) return null;
  return {
    id: parentTaskId.value,
    projectId: task.value.projectId,
    stageId: task.value.stageId,
    workspaceId: task.value.workspaceId,
    attachments: parentAttachments.value,
  };
});

// Computed properties from Vuex state
const mobileMode = computed(() => store.state.system.mobileMode);
const isTablet = computed(() => store.state.system.tabletSmallMode || store.state.system.tabletMode);
const user = computed(() => store.state.user.user);
const project = computed(() => store.state.project.project);
const taskUnderEdit = computed(() => store.state.project.taskUnderEdit);
const subtasks = computed(() => store.state.project.subtasks);
const subtasksListRef = ref(null);

const sortedSubtasks = computed(() => {
  if (!subtasks.value) return subtasks.value;
  return [...subtasks.value].sort((a, b) => (a.innerOrder ?? Infinity) - (b.innerOrder ?? Infinity));
});

// Computed getters from Vuex
const userIsProjectAdmin = computed(() => store.getters['project/userIsProjectAdmin']);
const getUser = computed(() => store.getters['user/getUser']);
const accessTrackingBrowser = computed(() => store.getters['user/accessTrackingBrowser']);

// Additional computed properties
const showChecklist = computed(() => {
  return checklistActive.value || (task.value.checklist && task.value.checklist !== '[]');
});

const ttbuOfCurrentUser = computed(() => {
  return task.value?.taskTrackedByUsers?.find(x => x.userId === user.value.id);
});

const taskId = computed(() => {
  return taskUnderEdit.value ? taskUnderEdit.value.id : route.params.taskSlug;
});



const task = computed(() => {
  return taskUnderEdit.value;
});

const isWsAdmin = computed(() => {
  if (!user.value) {
    return false;
  }
  return getUser.value(user.value.id).userWorkspace.role === 'ADMIN';
});

const checkedScreenshots = computed(() => {
  return task.value.screenshots ? task.value.screenshots.filter(el => el.capturedAt) : [];
});



const getPMs = computed(() => {
  if (!project.value || !project.value.projectMembers) {
    return [];
  }
  return project.value ? project.value.projectMembers
    .map(member => (member && member.userId ? getUser.value(member.userId) : null))
    .filter(member => member && !member.botClassId) : [];
});





const sortedScreenshots = computed(() => {
  if (task.value.screenshots) {
    const ss = [...task.value.screenshots];
    ss.sort((a, b) => dayjs(b.capturedAt) - dayjs(a.capturedAt));

    return ss.map((s) => {
      return { id: `list_scroller_${s.id}`, value: s };
    });
  }
  return [];
});


const sortedEvents = computed(() => {
  if (task.value && task.value.taskEvents) {
    const ev = [...task.value.taskEvents];
    ev.sort((a, b) => {
      if (dayjs(a.date) > dayjs(b.date)) return 1;
      if (dayjs(a.date) < dayjs(b.date)) return -1;
      if (a.id > b.id) return 1;
      if (a.id < b.id) return -1;

      return 0;
    });

    return ev;
  }
  return [];
});

const parentSortedEvents = computed(() => {
  const ev = [...parentEvents.value];
  ev.sort((a, b) => {
    if (dayjs(a.date) > dayjs(b.date)) return 1;
    if (dayjs(a.date) < dayjs(b.date)) return -1;
    return 0;
  });
  return ev;
});


// Vuex actions and mutations
const actionUpdateTask = (payload) => {
  store.dispatch(
    'project/actionUpdateTask', 
    { 
      ...payload, 
      projectId: project.value.id,
    }
  );
};
const actionDeleteTask = (payload) => store.dispatch('project/actionDeleteTask', payload);
const actionGetTask = (payload) => store.dispatch('project/actionGetTask', payload);
const actionGetElementsPaged = (payload) => store.dispatch('project/actionGetElementsPaged', payload);
const actionLoadWhoReacted = (payload) => store.dispatch('project/actionLoadWhoReacted', payload);
const actionUploadAttachment = (payload) => store.dispatch('project/actionUploadAttachment', payload);
const actionAttachImageToTask = (payload) => store.dispatch('project/actionAttachImageToTask', payload);
const actionCreateComment = (payload) => store.dispatch('project/actionCreateComment', payload);
const actionDeleteScreenshot = (payload) => store.dispatch('project/actionDeleteScreenshot', payload);
const actionUpdateComment = (payload) => store.dispatch('project/actionUpdateComment', payload);
const actionDeleteComment = (payload) => store.dispatch('project/actionDeleteComment', payload);
const actionDeleteAttachment = (payload) => store.dispatch('project/actionDeleteAttachment', payload);
const actionSetTaskPreview = (payload) => store.dispatch('project/actionSetTaskPreview', payload);
const actionMarkAsRead = (payload) => store.dispatch('project/actionMarkAsRead', payload);
const actionCleanUpd = (payload) => store.dispatch('project/actionCleanUpd', payload);
const startTracking = (payload) => store.dispatch('task/startTracking', payload);
const mutationShowTrackingInfo = (payload) => store.commit('task/mutationShowTrackingInfo', payload);
const actionGetSubtasksForTask = (payload) => store.dispatch('project/actionGetSubtasksForTask', payload);
const mutationSetSubtasksLoading = (parentTaskId) => store.commit('project/setSubtasksLoading', parentTaskId);
const actionGetTaskAndOpen = (payload) => store.dispatch('project/actionGetTaskAndOpen', payload);

// Methods
const pinnedTaskComments = ref([]);

const onCommentEditStateChanged = ({ commentId, state }) => {
  if (!commentId) return;
  const next = new Set(editingCommentIds.value);
  if (state) {
    next.add(commentId);
  } else {
    next.delete(commentId);
    lastCommentEditClosedAt.value = Date.now();
  }
  editingCommentIds.value = next;
};

const onCommentHiddenChanged = ({ commentId, state }) => {
  const next = new Set(hiddenCommentIds.value);
  if (state) {
    next.add(commentId);
  } else {
    next.delete(commentId);
  }
  hiddenCommentIds.value = next;
};

watch(pinnedTaskComments, () => {
  nextTick(() => {
    if (pinnedSwiper.value) {
      pinnedSwiper.value.update();
      pinnedActiveIndex.value = pinnedSwiper.value.realIndex;
    }
  });
}, { deep: true });

watch(taskId, () => {
  editingCommentIds.value = new Set();
  hiddenCommentIds.value = new Set();
  lastCommentEditClosedAt.value = 0;
});

const pinnedCommentIds = computed(() => {
  const list = Array.isArray(pinnedTaskComments.value) ? pinnedTaskComments.value : [];
  return new Set(list.map(p => p.commentId).filter(Boolean));
});

const isCommentPinned = (commentId) => {
  return !!commentId && pinnedCommentIds.value.has(commentId);
};

const onPinChanged = async (e) => {
  await loadPinnedTaskComments();
};

const loadPinnedTaskComments = async () => {
  if (!taskId.value) {
    pinnedTaskComments.value = [];
    return;
  }
  try {
    const res = await callAPI({
      method: 'getPinnedTaskComments',
      params: { taskId: taskId.value },
      async: true,
    });
    pinnedTaskComments.value = Array.isArray(res) ? res : [];
  } catch (e) {
    console.error('Failed to load pinned task comments', { taskId: taskId.value, error: e });
    pinnedTaskComments.value = [];
  }
};

const loadMoreScreenshots = async () => {
  if (loadingScreenshots.value) {
    return;
  }
  loadingScreenshots.value = true;
  const resp = await actionGetElementsPaged({
    category: 'screenshot',
    taskId: taskId.value,
    order: 'DESC',
    offset: checkedScreenshots.value.length,
    count: 40,
  });
  loadingScreenshots.value = false;
};

const loadParentAttachments = async () => {
  console.log('Loading parent attachments 🤲🤲🤲🤲🤲🤲 ', parentTaskId.value);
  if (!parentTaskId.value) return;
  try {
    const resp = await callAPI({
      method: 'getElementsPaged',
      params: {
        category: 'attachment',
        taskId: parentTaskId.value,
        offset: parentAttachments.value.length,
        count: 100,
        order: 'ASC',
      },
      async: true,
    });
    if (resp && resp.elements) {
      parentAttachments.value = [
        ...parentAttachments.value,
        ...resp.elements.filter(a => a.type !== 'TASK_PREVIEW' || a.uploaded),
      ];
    }
  } catch (e) {
    console.error('Error loading parent attachments', e);
  }
};

const initTask = async () => {
  taskLoaded.value = false;
  // reset parent side panel state for new task
  parentEvents.value = [];
  parentAttachments.value = [];
  parentLogsShown.value = false;
  const openFromNavigation = !!taskUnderEdit.value;
  const openInApp = !!window.APP_TRON;
  await actionGetTask(
    { id: taskUnderEdit.value ? taskUnderEdit.value.id : route.params.taskSlug },
  );
  store.dispatch('user/actionClearTaskNotifications', { taskId: task.value.id });
  await loadPinnedTaskComments();
  taskLoaded.value = true;
  elementsNextKey.value = task.value.taskEventsNextKey;

  // load reaction details after comments are rendered, off the main loading thread
  actionLoadWhoReacted({ taskId: task.value.id }).catch(e => console.error('Failed to load who reacted', e));

  if ((openFromNavigation || openInApp) && accessTrackingBrowser.value) {
    startTrack();
  }

  newCommentId.value = window.utils.genId();

  setTimeout(() => {
    if (task.value && !task.value.textMD) {
      nextTick(() => {
        if (fullTaskEditor.value) {
          // todo, check whether it is working at all
          fullTaskEditor.value.activate();
        }
      });
    }
    loadedTabNum.value += 1;
  }, 1200);

  // tried to replace with subscribe- we need to to fetch attachments
  nextTick(() => {
    const eventsBlock = document.getElementById('events');
    if (eventsBlock) {
      eventsBlock.addEventListener('scroll', resetScrollToTopButtonVisibility);
      eventsBlock.addEventListener('wheel', resetPinnedToLatest, { passive: true });
      eventsBlock.addEventListener('touchmove', resetPinnedToLatest, { passive: true });
    }
  });

  mutationShowTrackingInfo(true);
  cleanUpd();

  // Load subtasks asynchronously: set to null quickly, then fetch list
  if (task.value?.isParentTask) {
    // initialize loading state to show skeleton
    mutationSetSubtasksLoading(task.value.id);
    actionGetSubtasksForTask({ parentTaskId: task.value.id });
  } else {
    // ensure any old data does not linger visually
    mutationSetSubtasksLoading(task.value.id);
  }
};

const startTrack = () => {
  console.log('⭐⭐⭐⭐⭐ startTrack', accessTrackingBrowser.value);
  // start tracking
  if (accessTrackingBrowser.value) {
    startTracking({ task: task.value });
  }
};

const goToProjectSettings = () => {
  router.replace({ path: `/project/${project.value.slug}/${project.value.nameSlug}/settings/` });
};

const closeMobileRightBar = () => {
  rightBarOpen.value = false;
};

const closeIfNoChanges = () => {
  // Ignore ESC while comment editor is open or was just closed by the same key press.
  if (isEditComment.value || (Date.now() - lastCommentEditClosedAt.value < 250)) {
    return;
  }
  const hasChanges = [...document.querySelectorAll('.ql-editor')].some(q => !!q.innerText.trim());
  if (!hasChanges) {
    emit('closeMe');
  }
};

const markAsReadSingle = (comment) => {
  const ttbu = ttbuOfCurrentUser.value;
  if (ttbu && ttbu.commentsToRead > 0) {
    if (isread(comment)) {
      ttbu.commentsToRead -= 1;
      actionMarkAsRead({
        commentId: comment.id,
        ttbuId: ttbu.id,
        taskId: task.value.id,
      });
    }
  }
};

const cleanUpd = () => {
  setTimeout(() => {
    const ttbu = ttbuOfCurrentUser.value;
    if (!ttbu) return;
    actionCleanUpd({
      ttbuId: ttbu.id,
      taskId: task.value.id,
    });
  }, 1000);
};

const markAsRead = () => {
  const ttbu = ttbuOfCurrentUser.value;
  if (ttbu && ttbu.commentsToRead > 0) {
    const datatosend = {
      option: 'all',
      ttbuId: ttbu.id,
      taskId: task.value.id,
    };
    ttbu.commentsToRead = 0;
    actionMarkAsRead(datatosend);
  }
};

const isread = (comment) => {
  if (!task.value || !task.value.taskTrackedByUsers || !user.value) {
    return false;
  }
  const ttbuinfo = ttbuOfCurrentUser.value;
  if (!comment.commentReadByUsers || !ttbuinfo) {
    return false;
  }
  const crbu = comment.commentReadByUsers.find(el => el.ttbuId === ttbuinfo.id);
  if (crbu && ttbuinfo.commentsToRead > 0) {
    return true;
  }
  return false;
};

const commentAttachImage = (event) => {
  commentFiles.value = null;
  commentFiles.value = event;
};

// Separate paste handler function
const handleDocumentPaste = async (event) => {
  const items = event.clipboardData ? event.clipboardData.items : [];
    
  const plainText = event.clipboardData ? event.clipboardData.getData('text/plain') : '';
  const hasImageFile = Array.from(items).some(
    item => item.kind === 'file' && item.type.startsWith('image')
  );
  if (plainText.trim() && !hasImageFile) {
    return;
  }

  const pastedFiles = Array.from(items).filter(item => item.kind === 'file');
  if (pastedFiles.length && !hasImageFile) {
    const dataFiles = new DataTransfer();
    pastedFiles.forEach(item => dataFiles.items.add(item.getAsFile()));
    commentAttachImage(dataFiles.files);
    return;
  }

  if (window.APP_TRON?.readClipboardFiles && !pastedFiles.length && event.clipboardData.types.includes('text/uri-list')) {
    const clipboardFiles = await window.APP_TRON.readClipboardFiles();
    const dataFiles = new DataTransfer();
    clipboardFiles.forEach(file => dataFiles.items.add(new File([file.data], file.name, { type: file.type })));
    commentAttachImage(dataFiles.files);
    return;
  }

  // Fallback to regular clipboard processing for other types or if APP_TRON failed
  console.log('Paste ITEMS:', Array.from(items).map(item => item.type));
  // Fallback to regular clipboard processing for other types
  await processIncomingFiles(items, (e) => {
    console.log('Processing image through regular clipboard:', e);
    
    // Determine the correct MIME type and filename
    const mimeType = e.originalMimeType || 'image/png'; // Use original type if available
    const fileExtension = mimeType.split('/')[1] || 'png'; // Extract extension from MIME type
    const filename = `image.${fileExtension}`;
    
    // Handle different types of image objects
    let blob;
    if (e.image && typeof e.image.asBlob === 'function') {
      // Canvas or processed image with asBlob method
      blob = e.image.asBlob();
    } else if (e.image && e.image instanceof HTMLImageElement) {
      // Regular HTML Image element - convert to blob using canvas
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = e.width;
      canvas.height = e.height;
      ctx.drawImage(e.image, 0, 0);
      canvas.toBlob((canvasBlob) => {
        const file = new File([canvasBlob], filename, { type: mimeType });
        const dt = new DataTransfer();
        dt.items.add(file);
        commentAttachImage(dt.files);
      }, mimeType); // Specify the MIME type for canvas.toBlob
      return; // Exit early since toBlob is async
    } else {
      console.error('Unknown image type:', e.image);
      return;
    }
    
    const file = new File([blob], filename, { type: mimeType });
    const dt = new DataTransfer();
    dt.items.add(file);
    commentAttachImage(dt.files);
  },
  (e) => {
    console.log('Processing file through regular clipboard:', e);
    commentAttachImage(e);
  });
};

const toFileItem = (item) => {
  if (item?.kind !== 'file') return null;

  const file = item.getAsFile?.();
  if (!file) return null;

  return {
    kind: 'file',
    type: file.type || item.type || '',
    getAsFile: () => file,
  };
};

// Process incoming files function (converted from Vue 2 methods)
const processIncomingFiles = async (items, cbImg, cbFile, source) => {
  const itemsLength = items.length;
  console.log('Processing incoming files, items length:', itemsLength, 'source:', source);
  let itemsArray = Array.from(items);
  const originalFileItems = itemsArray.map(toFileItem).filter(Boolean);

  // Check for HTML string with base64 image when multiple items exist
  // e.g. inserted from win + .
  if (itemsLength > 1) {
    const hasFileItem = itemsArray.some(item => item.kind === 'file');
    const htmlItem = itemsArray.find(item => item.kind === 'string' && item.type === 'text/html');

    /// Ordering matters:
    /// - If an HTML item exists and either there is no file item or the HTML item is first,
    ///   handle HTML first (preserves pasting GIFs from Windows Win+. which arrive as HTML).
    /// - If a file item is first (e.g., Slack image attachments), handle the file first so image insertion works.

    if (htmlItem) {
      try {
        const htmlString = await new Promise((resolve) => {
          htmlItem.getAsString(resolve);
        });
        console.log('HTML string found in items:', htmlString);
        
        // Extract image URL from img tag in HTML (both data URLs and regular URLs)
        const imgTagMatch = htmlString.match(/<img[^>]+src="([^"]+)"/);
        if (imgTagMatch) {
          const imageUrl = imgTagMatch[1];
          console.log('Found image URL in HTML:', imageUrl);
          
          let syntheticItem;
          
          if (imageUrl.startsWith('http')) {
            // Handle regular URL - fetch the image
            console.log('Processing regular URL image from HTML, fetching:', imageUrl);
            try {
              const response = await fetch(imageUrl, { redirect: 'manual' });
              if (!response.ok && hasFileItem) {
                console.log('Fetch failed, falling back to original file item');
                itemsArray = originalFileItems;
              } else {
                const blob = await response.blob();
                
                // Determine the image type from the blob or URL
                let imageType = blob.type || 'image/gif';
                if (!imageType.startsWith('image/')) {
                  // Fallback: guess from URL extension
                  if (imageUrl.toLowerCase().includes('.gif')) imageType = 'image/gif';
                  else if (imageUrl.toLowerCase().includes('.png')) imageType = 'image/png';
                  else if (imageUrl.toLowerCase().includes('.jpg') || imageUrl.toLowerCase().includes('.jpeg')) imageType = 'image/jpeg';
                  else imageType = 'image/gif'; // default fallback
                }
                
                // For GIFs, we want to preserve animation, so we'll create a special synthetic item
                // that directly processes the blob without going through image conversion
                if (imageType === 'image/gif') {
                  console.log('Creating synthetic GIF item with preserved animation');
                  syntheticItem = {
                    kind: 'file',
                    type: 'image/gif',
                    getAsFile: () => blob,
                    // Add a special flag to indicate this should be processed differently
                    isAnimatedGif: true,
                    directBlob: blob
                  };
                } else {
                  syntheticItem = {
                    kind: 'file',
                    type: imageType,
                    getAsFile: () => blob
                  };
                }
              }
            } catch (error) {
              console.error('Failed to fetch image from URL:', imageUrl, error);
              // Don't create synthetic item if fetch fails, fall back to original items
            }
          } else {
            console.error('Unsupported PASTE image URL format:', imageUrl);
          }
          
          // Replace itemsArray with just the synthetic item if we successfully created one
          if (syntheticItem) {
            console.log('Using synthetic item instead of multiple items');
            itemsArray = [syntheticItem];
          }
        }
        } catch (error) {
          console.error('Error processing HTML item:', error);
        }
    }
  }

  // How many actual images are in this batch (not just raw clipboard item count, which can
  // include a non-image text/html representation of the same image, or already-collapsed
  // synthetic items) — used to skip Painterro for multi-image pastes instead of only
  // annotating the first image and silently dropping the rest.
  const imageItemsCount = itemsArray.filter(i => i.kind === 'file' && i.type
    && i.type.startsWith('image') && !i.type.includes('svg')).length;

  // Live DataTransferItem entries only stay valid synchronously — once the loop below
  // awaits (e.g. reading item[0]'s file as a data URL), the browser invalidates the rest
  // of the list and their .kind/.type start returning "". Swap any remaining live items
  // for their already-safely-extracted (toFileItem) equivalent before that can happen.
  // Synthetic items produced above (gif/html handling) aren't DataTransferItem instances,
  // so they pass through untouched — re-wrapping them would drop isAnimatedGif/directBlob.
  itemsArray = itemsArray.map(item => (
    item instanceof DataTransferItem && item.kind === 'file' ? (toFileItem(item) || item) : item
  ));

  for (let order = 0; order < itemsArray.length; order++) {
    const item = itemsArray[order];
    console.log('🟢🟢🟢🟢TaskDialog processIncomingFiles ', {
      item,
      kind: item.kind,
      type: item.type,
    });
    
    if (item.kind === 'file') {
      const blob = item.getAsFile ? item.getAsFile() : item;
      const itemType = item.type;
      
      // Special handling for animated GIFs fetched from URLs
      if (item.isAnimatedGif && item.directBlob) {
        console.log('Processing animated GIF with preserved animation');
        try {
          const dataUrl = await readFileAsDataURL(item.directBlob);
          // For animated GIFs, we want to bypass the Image() conversion that loses animation
          // and directly create the response object
          const imgResponse = {
            // Use the blob directly instead of converting through Image
            image: {
              asBlob: () => item.directBlob,
              // Create a dummy Image object for size detection
              width: 0, // We'll set reasonable defaults
              height: 0
            },
            data: dataUrl,
            width: 498, // Default GIF size - could be improved by reading actual dimensions
            height: 238,
            updTaskId: task.value.id,
            updProjectId: task.value.projectId,
            updStageId: task.value.stageId,
            updWorkspaceId: task.value.workspaceId,
            hasAttachments: task.value.attachments
              && !!task.value.attachments.find(a => a.type === 'TASK_PREVIEW'),
            commentId: window.utils.genId(),
            order,
            originalMimeType: itemType, // Preserve original MIME type
          };
          cbImg(imgResponse);
        } catch (error) {
          console.error('Error processing animated GIF:', error);
        }
        continue;
      }
      
      if (
        itemType.startsWith('image')
        && !itemType.includes('svg')
      ) {
        try {
          const dataUrl = await readFileAsDataURL(blob);
          console.log('TaskDialog processIncomingFiles 🟢', dataUrl, itemType, itemsLength, source);
          if (itemType.includes('gif') || source === 'drop' || imageItemsCount > 1) {
            const img = new Image();
            img.onload = function sizeDiscoverer() {
              const imgResponse = {
                image: this,
                data: dataUrl,
                width: this.width,
                height: this.height,
                updTaskId: task.value.id,
                updProjectId: task.value.projectId,
                updStageId: task.value.stageId,
                updWorkspaceId: task.value.workspaceId,
                hasAttachments: task.value.attachments
                  && !!task.value.attachments.find(a => a.type === 'TASK_PREVIEW'),
                commentId: window.utils.genId(),
                order,
                originalMimeType: itemType, // Preserve original MIME type
              };
              cbImg(imgResponse);
            };
            img.src = dataUrl;
          } else {
            if (painterroOpened.value
              || window.painterroOpenedInstance
              || store.state.system.openedDialog === 'CreateTask'
            ) {
              continue;
            }
            painterroOpened.value = true;
            emit('update:painterroOpened', painterroOpened.value);
            if (document.activeElement) {
              document.activeElement.blur();
            }

            editImageWithPainterro({
              imageUrl: dataUrl,
              onSave: async (image) => {
                cbImg({
                  image,
                  attachmentId: window.utils.genId(),
                  data: image.asDataURL(),
                  width: image.getWidth(),
                  height: image.getHeight(),
                  updTaskId: task.value.id,
                  updProjectId: task.value.projectId,
                  updStageId: task.value.stageId,
                  updWorkspaceId: task.value.workspaceId,
                  hasAttachments: task.value.attachments
                    && !!task.value.attachments.find(a => a.type === 'TASK_PREVIEW'),
                  commentId: newCommentId.value,
                  order,
                  originalMimeType: itemType, // Preserve original MIME type
                }, 'img');
                painterroOpened.value = false;
                emit('update:painterroOpened', painterroOpened.value);
              },
              onClose: () => {
                painterroOpened.value = false;
                emit('update:painterroOpened', painterroOpened.value);
              },
            });
          }
        } catch (error) {
          console.error('Error reading image file:', error);
        }
      } else {
        try {
          const dataUrl = await readFileAsDataURL(blob);
          cbFile({
            blob,
            attachmentId: window.utils.genId(),
            filename: blob.name,
            type: itemType,
            data: dataUrl,
            size: blob.size,
            taskId: task.value.id,
            stageId: task.value.stageId,
            projectId: task.value.projectId,
            workspaceId: task.value.workspaceId,
            commentId: window.utils.genId(),
          }, 'file');
        } catch (error) {
          console.error('Error reading file:', error);
        }
      }
    }
  }
};

const updateChecklist = (e) => {
  actionUpdateTask({
    id: task.value.id,
    stageId: task.value.stageId,
    projectId: task.value.projectId,
    checklist: JSON.stringify(e),
  });
};

const attachFilesToComment = (commentId, files) => processIncomingFiles(Array.from(files, file => ({
  kind: 'file',
  type: file.type,
  getAsFile: () => file,
})), async (e) => {
  e.commentId = commentId;
  await actionAttachImageToTask(e);
}, async (e) => {
  e.commentId = commentId;
  await actionUploadAttachment({
    blobData: e.blob,
    taskId: task.value.id,
    stageId: task.value.stageId,
    projectId: task.value.projectId,
    commentId: e.commentId,
    workspaceId: getUser.value(user.value.id).userWorkspace.workspaceId,
    attachmentId: window.utils.genId(),
    filename: e.blob.name,
  });
}, 'drop');

const submitWithFiles = (submitData) => {
  const commentId = newCommentId.value;
  addComment(submitData);
  setTimeout(() => attachFilesToComment(commentId, submitData.files), 5);
};

const deleteComment = (e) => {
  const { text, ...comment } = e;
  if (task.value && task.value.attachments && task.value.attachments.length) {
    const main = task.value.attachments.find(a => a.type === 'TASK_PREVIEW');
    if (main && text.includes(main.id)) {
      actionDeleteAttachment({
        id: main.id,
        taskId: task.value.id,
        projectId: task.value.projectId,
        stageId: task.value.stageId,
        type: main.type,
        extension: main.extension,
      });
    }
  }
  actionDeleteComment(comment);

  pinnedTaskComments.value = pinnedTaskComments.value.filter(p => p.commentId !== comment.id);
};

// Lifecycle hooks
onMounted(async () => {
  await nextTick();
  readyToTeleport.value = true;
  // Removed mobile swipe open/close handlers (right/left) to avoid Android system gesture conflicts.
  // Bind document paste event listener
  console.log(' 👷‍♂️MOUNTING TASK paste handler');
  document.addEventListener('paste', handleDocumentPaste);
  await initTask();
  if (subtasks.value) nextTick(initSubtaskSortable);
});

onBeforeUnmount(() => {
  if (document.getElementById('events')) {
    document.getElementById('events')
      .removeEventListener('scroll', resetScrollToTopButtonVisibility);
    document.getElementById('events').removeEventListener('wheel', resetPinnedToLatest);
    document.getElementById('events').removeEventListener('touchmove', resetPinnedToLatest);
  }
  
  // Remove document paste event listener
  document.removeEventListener('paste', handleDocumentPaste);

  destroySubtaskSortable();

  mutationShowTrackingInfo(false);
});

// I'll need to add the remaining methods here
// Due to the file size, I'll focus on the most critical methods
// and provide a framework for converting the rest

const resetScrollToTopButtonVisibility = () => {
  const el = events.value.$el;
  showScrollToTopButton.value = el.scrollTop - (el.clientHeight - el.scrollHeight) > 24;
};

const scrollEventsToTop = () => {
  const el = events.value.$el;
  el.scrollTo({ top: el.clientHeight - el.scrollHeight, behavior: 'smooth' });
};

const scrollEventsToBottom = () => {
  nextTick(() => {
    const el = events.value?.$el;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  });
};




const addComment = (e) => {
  if (!e.text.trim() && (!e.files || !e.files.length)) {
    return;
  }
  if (e.delta) {
    const transfer = e.delta.ops.filter((obj) => {
      if (obj.insert.mention) {
        return true;
      }
      return false;
    })[0];
    if (transfer) {
      const userId = transfer.insert?.mention?.id;
      if (userId && userId !== user.value.id) {
        transferUser.value = userId;
      }
    }
  }
  actionCreateComment({ id: newCommentId.value, text: e, task: task.value });
  newCommentId.value = window.utils.genId();
  scrollEventsToBottom();
};



const loadEvents = async (category, offset, count, called, order, cb) => {
  if (elementsNextKey.value === null || elementsNextKey.value === undefined) {
    console.warn('No more elementsNextKey');
    cb({ offset: null });
    return
  }
  try {
    loading.value = true;
    console.log('taskId', taskId.value);
    const resp = await actionGetElementsPaged({
      category,
      taskId: taskId.value,
      offset: elementsNextKey.value,
      alreadyLoadedElements: checkedScreenshots.value.length,
      count,
      called,
      order,
    });
    elementsNextKey.value = resp.offset;

    cb(resp);
    // load reaction details for the newly paged-in comments, off the main thread
    actionLoadWhoReacted({ taskId: taskId.value }).catch(e => console.error('Failed to load who reacted', e));
    await nextTick();
    // scroll to top
  } catch (error) {
    console.error('Error loading events:', error);
  } finally {
    loading.value = false;
    // resetScrollToTopButtonVisibility();
  }
};

const loadParentEvents = async (category, offset, count, called, order, cb) => {
  console.log('🤲🤲🤲🤲🤲 Loading parent events for parentTaskId:', parentTaskId.value, {offset, count});
  if (!parentTaskId.value) {
    cb({offset});
    return;
  }
  if (offset === null) {
    // nothign more to load
    cb({offset});
    return;
  }
  try {
    const resp = await callAPI({
      method: 'getElementsPaged',
      params: {
        category: 'taskEvent',
        taskId: parentTaskId.value,
        offset,
        count,
        called,
        order,
      },
      async: true,
    });
    if (resp && resp.elements) {
      parentEvents.value = [...parentEvents.value, ...resp.elements];

      cb(resp);

    }
  } catch (e) {
    console.error('Error loading parent events', e);
  }
  
};

const onDrop = (event) => {
  event.preventDefault();
  if (!event.dataTransfer.types.includes('Files')) return;
  const dataFiles = new DataTransfer();
  dnd.value.classList.remove('active');
  const items = event.dataTransfer.items || event.dataTransfer.files;
  Array.from(items).forEach((item) => {
    const blob = item.getAsFile ? item.getAsFile() : item;
    dataFiles.items.add(blob);
  });
  eventBus.$emit('drop-files', dataFiles.files);
};

const onDragLeave = (event) => {
  event.preventDefault();
  dnd.value.classList.remove('active');
};

const onDragEnter = (event) => {
  event.preventDefault();
  if (!event.dataTransfer.types.includes('Files')) return;
  dnd.value.classList.add('active');
};

const onDragOver = (event) => {
  event.preventDefault();
};

const openSubtask = (st) => {
  if (!st || !st.id) return;
  actionGetTaskAndOpen({ id: st.id });
};

let subtaskSortable = null;

function destroySubtaskSortable() {
  if (subtaskSortable) {
    subtaskSortable.destroy();
    subtaskSortable = null;
  }
}

function initSubtaskSortable() {
  destroySubtaskSortable();
  if (!subtasksListRef.value) return;

  subtaskSortable = new Sortable(subtasksListRef.value, {
    scroll: true,
    scrollSensitivity: mobileMode.value ? 100 : 200,
    scrollSpeed: mobileMode.value ? 5 : 10,
    sort: true,
    delay: 250,
    delayOnTouchOnly: true,
    touchStartThreshold: 8,
    animation: 150,
    easing: "cubic-bezier(1, 0, 0, 1)",
    handle: ".task-wrapper",
    draggable: ".task-wrapper",
    filter: ".nodrag",
    preventOnFilter: true,
    ghostClass: "sortable-ghost",
    chosenClass: "sortable-chosen",
    dragClass: "sortable-drag",
    forceFallback: true,
    fallbackTolerance: 8,
    onEnd: onSubtaskDragEnd,
  });
}

function onSubtaskDragEnd(evt) {
  if (evt.oldIndex === evt.newIndex) return;

  const newList = [...sortedSubtasks.value];
  const [moved] = newList.splice(evt.oldIndex, 1);
  newList.splice(evt.newIndex, 0, moved);
  store.commit('project/setSubtasks', newList.map((s, i) => ({ ...s, innerOrder: i })));

  callAPI({
    method: 'reorderSubtasks',
    params: { parentTaskId: task.value.id, orderedIds: newList.map(s => s.id) },
    persisted: true,
  });
}

watch(() => subtasks.value && subtasks.value.length, () => {
  nextTick(initSubtaskSortable);
});

const returnToParent = () => {
  if (!task.value?.parentTaskId) return;
  actionGetTaskAndOpen({ id: task.value.parentTaskId });
};



const onDeleteTask = () => {
  actionDeleteTask({
    ...task.value,
  });
  emit('closeMe');
};



const openAppRunner = () => {
  // Note: This needs to be adapted for Vue 3 as $root is not available
  // You might need to use provide/inject or emit to parent
  console.log('openAppRunner - needs implementation for Vue 3');
};

const isCommentSameUser = (index) => {
  const prevComment = sortedEvents.value[index - 1];
  const currentCommnet = sortedEvents.value[index];
  if (!prevComment || !currentCommnet) return true;
  if (!prevComment.taskComment || !currentCommnet.taskComment) return true;
  if (prevComment.taskComment.userId === currentCommnet.taskComment.userId) {
    const betweenTime = (new Date(currentCommnet.taskComment.createdAt).getTime()
      - new Date(prevComment.taskComment.createdAt).getTime()) / 60000;

    if (betweenTime < 10) return false;
  }
  return true;
};

const isParentCommentSameUser = (index) => {
  const prevComment = parentSortedEvents.value[index - 1];
  const currentCommnet = parentSortedEvents.value[index];
  if (!prevComment || !currentCommnet) return true;
  if (!prevComment.taskComment || !currentCommnet.taskComment) return true;
  if (prevComment.taskComment.userId === currentCommnet.taskComment.userId) {
    const betweenTime = (new Date(currentCommnet.taskComment.createdAt).getTime()
      - new Date(prevComment.taskComment.createdAt).getTime()) / 60000;

    if (betweenTime < 10) return false;
  }
  return true;
};

const isTaskCommentLoaded = (commentId) => {
  const events = task.value?.taskEvents || [];
  return events.some(e => e?.taskComment?.id === commentId);
};

const loadMoreUntil = async (isFound, { maxPages = 25, count = 50 } = {}) => {
  if (!taskId.value) return false;
  if (isFound()) return true;

  for (let pages = 0; pages < maxPages && elementsNextKey.value !== null && elementsNextKey.value !== undefined; pages += 1) {
    try {
      const resp = await actionGetElementsPaged({
        category: 'taskEvent',
        taskId: taskId.value,
        offset: elementsNextKey.value,
        count,
        order: eventsAreReversed.value ? 'DESC' : 'ASC',
        alreadyLoadedElements: task.value?.taskEvents?.length || 0,
      });
      elementsNextKey.value = resp.offset;
    } catch (e) {
      console.error('Failed to load more events for scroll', { error: e });
      return false;
    }

    await nextTick();
    if (isFound()) return true;
  }

  return isFound();
};

const highlightAndScrollTo = async (elementId) => {
  await nextTick();
  const el = document.getElementById(elementId);
  if (!el) {
    console.warn('Scroll target not found in DOM', { elementId });
    return;
  }

  highlightedElementId.value = elementId;
  if (highlightTimeoutId.value) {
    clearTimeout(highlightTimeoutId.value);
  }
  
  highlightTimeoutId.value = setTimeout(() => {
    highlightedElementId.value = null;
    highlightTimeoutId.value = null;
  }, 3000);

  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

const scrollToPinnedComment = async (commentId) => {
  if (!commentId) return;

  // ensure comments log is visible
  if (screeenshotsIsOpen.value) {
    screeenshotsIsOpen.value = false;
  }

  const ok = await loadMoreUntil(() => isTaskCommentLoaded(commentId));
  if (!ok) {
    console.warn('Pinned scroll: comment not found after paging', { commentId });
    return;
  }

  await highlightAndScrollTo(`task-comment-${commentId}`);
};

const scrollToManualTime = async () => {
  logsShown.value = true;
  screeenshotsIsOpen.value = false;

  const manualTimeEvents = () => sortedEvents.value
    .filter(e => MANUAL_TIME_ACTIONS.includes(e.action))
    .reverse();

  const loaded = manualTimeEvents();
  const nextIndex = loaded.findIndex(e => e.id === lastManualTimeEventId) + 1;
  if (nextIndex === loaded.length) {
    await loadMoreUntil(() => manualTimeEvents().length > loaded.length);
  }

  const found = manualTimeEvents();
  const target = found[nextIndex] || found[0];
  lastManualTimeEventId = target.id;
  await highlightAndScrollTo(eventElementId(target));
};

// Watchers
watch(() => task.value?.id, async (newId, oldId) => {
  if (newId !== oldId && taskLoaded.value) {
    await initTask();
  }
});
</script>

